import { businessDateOf, businessDay, parseLocalDate } from './time'

/**
 * S8 (CMP-4, GDPR): when a customer's personal data may be erased. Pure: the
 * hourly retention job, an erasure request's preview and the request itself
 * ask the same questions of the same facts, so what the Owner is shown and
 * what the job then does cannot disagree. Every period is configuration the
 * lawyer sets (Q-46, Q-117; CLAUDE.md: never hardcode a time): absent, null or
 * out of range, nothing in that class is ever erased.
 */

/** The four settings: their key and the range the loader accepts, in days. */
export const RETENTION_SETTINGS = {
  /** Q-117: ID and licence photos, counted from the end of the booking they came with. */
  identityDocuments: { key: 'retention.identity_documents_days', min: 0, max: 3650 },
  /** Inspection and damage photos, from their booking's end; never while their damage is unrepaired. */
  vehiclePhotos: { key: 'retention.vehicle_photos_days', min: 0, max: 3650 },
  /**
   * A hold, not a period: a renter's identity is kept at least this long after
   * each return, so that a fine reaching the company later can still name the
   * driver (art. 126-bis C.d.S.). Null keeps it indefinitely.
   */
  fineWindow: { key: 'retention.fine_window_days', min: 1, max: 3650 },
  /** The customer, from their last activity. The floor of 30 catches a days-for-months slip. */
  customer: { key: 'retention.customer_days', min: 30, max: 3650 },
} as const

export type RetentionSettingName = keyof typeof RETENTION_SETTINGS

/**
 * A stored setting read fail-closed: a whole number of days within its range,
 * or null (never erased). The loader refuses anything else; this is the
 * runtime's own check, as `notificationSetting` is for S7.
 */
export function retentionSetting(name: RetentionSettingName, value: unknown): number | null {
  const { min, max } = RETENTION_SETTINGS[name]
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
    ? value
    : null
}

/**
 * The instant before which an anchor is past a period of `days`: the start of
 * the Rome day `days` days before today's. Whatever is anchored on a day is
 * kept for the rest of that day and `days` days more, and goes from the next:
 * whole days in Rome, so a clock change moves nothing. A query compares its
 * anchor with this one instant, which an index can serve.
 */
export function retentionCutoff(days: number, now: Date): Date {
  const today = parseLocalDate(businessDateOf(now))
  return businessDay(today.subtract({ days }).toString()).start
}

export function pastRetention(anchor: Date, days: number, now: Date): boolean {
  return anchor.getTime() < retentionCutoff(days, now).getTime()
}

/** The instant an anchor passes a period of `days`: the start of the Rome day after its last. */
export function retentionDueAt(anchor: Date, days: number): Date {
  const last = parseLocalDate(businessDateOf(anchor)).add({ days })
  return businessDay(last.toString()).end
}

/**
 * A request is recorded, then waits this long before anything goes, so that
 * a mistake (the wrong customer among two of the same name) can be withdrawn.
 * Real hours: the Owner's "tomorrow at this time".
 */
export const ERASURE_GRACE_MS = 24 * 3_600_000

/**
 * Q-130b (owner, 28 Oct): a saved reply to a retried request may name the
 * customer, so it is emptied this long after it was saved. The key stays,
 * and a retry after that is refused (409), never run a second time.
 */
export const SAVED_REPLY_KEPT_MS = 7 * 24 * 3_600_000

/**
 * Q-165: a message from the website's contact form is erased in the same
 * transaction that e-mails it to the office. One that could not be sent (the
 * mailbox setting removed, the mail server down past every retry) keeps its
 * text this long, for Sistema's re-send, and is then erased unsent.
 */
export const CONTACT_MESSAGE_KEPT_MS = 30 * 24 * 3_600_000

export function erasureGraceEndsAt(recordedAt: Date): Date {
  return new Date(recordedAt.getTime() + ERASURE_GRACE_MS)
}

/**
 * An anonymised customer's first and last name. The record stays, so every
 * screen and document that prints a name still has one, and a database check
 * pins it (migration 0044).
 */
export const ANONYMIZED_NAME = '—'

/**
 * A customer's name as every screen prints it. An anonymised record names
 * nobody: it prints as the one mark, never as "— —".
 */
export function customerName(person: { firstName: string; lastName: string }): string {
  return person.firstName === ANONYMIZED_NAME && person.lastName === ANONYMIZED_NAME
    ? ANONYMIZED_NAME
    : `${person.firstName} ${person.lastName}`
}

/**
 * Why a booking's data must stay, in the order the Owner is told. Each is the
 * code's existing definition of "open", read by the server: a booking not yet
 * closed, cancelled or marked no-show; money owed either way; a deposit held;
 * a refund on its way; a card dispute open; a payment page that can still take
 * money; a difference with Stripe not settled; a fine on its rental still
 * open, or one that may still be matched to it; a CaRGOS report not answered,
 * or whose answer is unknown.
 */
export const BOOKING_HOLDS = [
  'not_ended',
  'balance',
  'deposit',
  'refund',
  'dispute',
  'payment_page',
  'stripe_difference',
  'fine',
  'cargos',
] as const
export type BookingHold = (typeof BOOKING_HOLDS)[number]

export interface BookingHoldFacts {
  ended: boolean
  balanceCents: number
  liveDeposit: boolean
  refundInFlight: boolean
  openDispute: boolean
  paymentPageOpen: boolean
  stripeDifferenceOpen: boolean
  openFine: boolean
  cargosPending: boolean
}

export function bookingHolds(facts: BookingHoldFacts): BookingHold[] {
  const held: Record<BookingHold, boolean> = {
    not_ended: !facts.ended,
    balance: facts.balanceCents !== 0,
    deposit: facts.liveDeposit,
    refund: facts.refundInFlight,
    dispute: facts.openDispute,
    payment_page: facts.paymentPageOpen,
    stripe_difference: facts.stripeDifferenceOpen,
    fine: facts.openFine,
    cargos: facts.cargosPending,
  }
  return BOOKING_HOLDS.filter((hold) => held[hold])
}

/**
 * What only a customer can be held by: the Owner's legal hold, a request's
 * grace, and, for the identity, the fine window.
 */
export const CUSTOMER_HOLDS = ['legal_hold', 'request_grace', 'fine_window'] as const
export type CustomerHold = (typeof CUSTOMER_HOLDS)[number]

/** What only a file can be held by: a vehicle photo of a damage not yet repaired. */
export const FILE_HOLDS = ['damage'] as const
export type FileHold = (typeof FILE_HOLDS)[number]

/** Every hold, in the order the Owner is told. */
export const PRIVACY_HOLDS = [...BOOKING_HOLDS, ...CUSTOMER_HOLDS, ...FILE_HOLDS] as const
export type PrivacyHold = (typeof PRIVACY_HOLDS)[number]

function inOrder(holds: Iterable<PrivacyHold>): PrivacyHold[] {
  const named = new Set(holds)
  return PRIVACY_HOLDS.filter((hold) => named.has(hold))
}

/**
 * A customer is erased in two parts. The contact (e-mail, phone, notes, the
 * free text of their bookings, their Stripe Customers) goes as soon as nothing
 * needs to reach them: a fine needs the name and the address, never the
 * e-mail. The identity (name, birth, address, licences, the identities
 * recorded for CaRGOS, imported rows, ID photos) waits for the fine window too.
 */
export type ErasurePart = 'contact' | 'identity'

/** A CaRGOS answer concerns the identity sent to the police, not how to reach the customer. */
const CONTACT_IGNORES: readonly BookingHold[] = ['cargos']

export interface CustomerHoldFacts {
  /** Every hold on a booking they made or appear on as a second driver. */
  bookingHolds: readonly BookingHold[]
  legalHold: boolean
  /** When the request was recorded; null with none, or once withdrawn. */
  requestRecordedAt: Date | null
  /** Whether a vehicle was ever handed over to them: only a renter can be named for a fine. */
  everRented: boolean
  /** Their latest return; null when they never brought one back. */
  lastReturnAt: Date | null
  fineWindowDays: number | null
  now: Date
}

export function customerHolds(part: ErasurePart, facts: CustomerHoldFacts): PrivacyHold[] {
  const booking = [...new Set(facts.bookingHolds)].filter(
    (hold) => part === 'identity' || !CONTACT_IGNORES.includes(hold),
  )
  const holds: PrivacyHold[] = BOOKING_HOLDS.filter((hold) => booking.includes(hold))
  if (facts.legalHold) holds.push('legal_hold')
  if (
    facts.requestRecordedAt !== null &&
    facts.now.getTime() < erasureGraceEndsAt(facts.requestRecordedAt).getTime()
  ) {
    holds.push('request_grace')
  }
  if (part === 'identity' && facts.everRented && !fineWindowPassed(facts)) {
    holds.push('fine_window')
  }
  return holds
}

/** Fail-closed: no window set, or no return yet, keeps a renter's identity. */
function fineWindowPassed(facts: CustomerHoldFacts): boolean {
  if (facts.fineWindowDays === null || facts.lastReturnAt === null) return false
  return pastRetention(facts.lastReturnAt, facts.fineWindowDays, facts.now)
}

/** What happens to one part of a customer, as the Owner is shown it and as the job acts on it. */
export type ErasureVerdict =
  | { state: 'erased'; at: Date }
  | { state: 'held'; holds: PrivacyHold[] }
  | { state: 'erasable' }
  /** No request: kept until its period ends. */
  | { state: 'kept'; until: Date }
  /** No request and no period set: kept until the lawyer sets one. */
  | { state: 'not_configured' }

export interface CustomerErasureFacts extends CustomerHoldFacts {
  erasedAt: Date | null
  /** Their latest activity; null while a booking of theirs is live (held anyway). */
  lastActivityAt: Date | null
  customerDays: number | null
}

export function erasureVerdict(part: ErasurePart, facts: CustomerErasureFacts): ErasureVerdict {
  if (facts.erasedAt !== null) return { state: 'erased', at: facts.erasedAt }
  const holds = customerHolds(part, facts)
  if (holds.length > 0) return { state: 'held', holds }
  // A recorded request past its grace: everything nothing holds goes now.
  if (facts.requestRecordedAt !== null) return { state: 'erasable' }
  if (facts.customerDays === null) return { state: 'not_configured' }
  if (facts.lastActivityAt === null) return { state: 'held', holds: ['not_ended'] }
  return pastRetention(facts.lastActivityAt, facts.customerDays, facts.now)
    ? { state: 'erasable' }
    : { state: 'kept', until: retentionDueAt(facts.lastActivityAt, facts.customerDays) }
}

/** What happens to a file a booking came with: a photo of an ID or a licence, or of the vehicle. */
export type FileVerdict =
  | { state: 'held'; holds: PrivacyHold[] }
  | { state: 'erasable' }
  | { state: 'kept'; until: Date }
  /** No period set: kept until the lawyer sets one. */
  | { state: 'not_configured' }

export interface BookingFileFacts {
  /** The booking's holds, the Owner's legal hold on anyone on it, and the file's own. */
  holds: readonly PrivacyHold[]
  /** When the booking was closed, cancelled or marked a no-show; null while it runs. */
  endedAt: Date | null
  /** The class's period: `identityDocuments` or `vehiclePhotos`. */
  days: number | null
  now: Date
}

/**
 * A file goes once its class's period has passed since its booking ended,
 * as soon as nothing holds it. Past its date and held, it says why, for
 * Sistema. Nothing else takes a vehicle photo; an ID photo also goes with its
 * customer's identity.
 */
export function bookingFileVerdict(facts: BookingFileFacts): FileVerdict {
  if (facts.days === null) return { state: 'not_configured' }
  if (facts.endedAt === null)
    return { state: 'held', holds: inOrder(['not_ended', ...facts.holds]) }
  if (!pastRetention(facts.endedAt, facts.days, facts.now)) {
    return { state: 'kept', until: retentionDueAt(facts.endedAt, facts.days) }
  }
  return facts.holds.length > 0
    ? { state: 'held', holds: inOrder(facts.holds) }
    : { state: 'erasable' }
}
